import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote, Shield } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

export default function AboutPage() {
  const [team, setTeam] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    axios.get(`${API}/team`).then(res => { if (Array.isArray(res.data)) setTeam(res.data); }).catch(console.error);
    axios.get(`${API}/testimonials`).then(res => { if (Array.isArray(res.data)) setTestimonials(res.data); }).catch(console.error);
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };
  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div data-testid="about-page">
      {/* Header */}
      <section className="pt-24 md:pt-32 pb-8 md:pb-16 relative">
        <div className="absolute inset-0 bg-surface grid-bg" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.p {...fadeIn} className="text-xs uppercase tracking-[0.3em] text-brand-accent font-bold mb-4 md:mb-6">
            Who We Are
          </motion.p>
          <motion.h1
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-heading font-black tracking-tight leading-[0.9] mb-6 md:mb-8"
          >
            The Minds
            <br />
            <span className="text-gradient-cyan">Behind</span> It
          </motion.h1>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-14 md:py-24 section-glow">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <motion.div {...fadeUp} className="lg:col-span-7">
              <h2 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-8">
                Built by researchers.
                <br />
                Driven by impact.
              </h2>
              <div className="space-y-6 text-slate-400 text-sm leading-relaxed">
                <p>
                  Artificial Mind was founded with a singular vision: to bridge the gap between cutting-edge AI research and real-world enterprise needs. We saw traditional industries struggling with digital transformation while academic breakthroughs remained locked in papers.
                </p>
                <p>
                  Our team of PhDs, former military intelligence officers, and senior engineers from leading tech companies came together to solve this. We don&apos;t just build models&mdash;we build AI systems that operate in the harshest, most demanding environments on earth.
                </p>
                <p>
                  From classified defense installations to remote oil rigs, from high-speed rail networks to low-orbit satellites, our solutions run where failure is not an option.
                </p>
              </div>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="glass-card rounded-lg p-8 space-y-8">
                <div>
                  <p className="text-4xl font-heading font-black text-white mb-1">2018</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Founded</p>
                </div>
                <div className="border-t border-white/5 pt-8">
                  <p className="text-4xl font-heading font-black text-white mb-1">280+</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Team Members</p>
                </div>
                <div className="border-t border-white/5 pt-8">
                  <p className="text-4xl font-heading font-black text-white mb-1">12</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Global Offices</p>
                </div>
                <div className="border-t border-white/5 pt-8">
                  <p className="text-4xl font-heading font-black text-white mb-1">47</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Countries Served</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section data-testid="team-section" className="py-14 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-4">Leadership</p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
              The <span className="text-gradient-cyan">Team</span>
            </h2>
          </motion.div>

          <motion.div {...fadeUp} className="glass-card rounded-lg p-10 lg:p-14 text-center max-w-2xl mx-auto">
            <Shield className="w-12 h-12 text-brand-accent mx-auto mb-6 opacity-70" />
            <h3 className="text-xl font-heading font-semibold mb-4">Classified Personnel</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              Due to the sensitive nature of our work across defense, intelligence, and government projects, our team members&apos; identities are kept confidential. Our leadership includes former AI research leads from top labs, retired military intelligence officers, and senior engineers from leading technology companies.
            </p>
            <p className="text-slate-500 text-xs mt-6 uppercase tracking-widest">
              280+ team members across 12 global offices
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-14 md:py-24 border-t border-white/5 section-glow">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-4">Client Voices</p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
              What They <span className="text-gradient-cyan">Say</span>
            </h2>
          </motion.div>

          {testimonials.length > 0 && (
            <motion.div {...fadeUp} className="max-w-3xl">
              <div className="glass-card rounded-lg p-10 lg:p-14 relative">
                <Quote className="w-10 h-10 text-white/10 absolute top-8 right-8" />
                <p className="text-xl md:text-2xl font-heading font-light leading-relaxed text-white mb-8">
                  &ldquo;{testimonials[currentTestimonial]?.quote}&rdquo;
                </p>
                <div>
                  <p className="font-heading font-semibold text-white">{testimonials[currentTestimonial]?.name}</p>
                  <p className="text-sm text-slate-500">
                    {testimonials[currentTestimonial]?.role}, {testimonials[currentTestimonial]?.company}
                  </p>
                </div>

                <div className="flex items-center gap-4 mt-8 pt-8 border-t border-white/5">
                  <button
                    onClick={prevTestimonial}
                    data-testid="testimonial-prev"
                    className="w-10 h-10 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-white/30 transition-colors duration-200"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={nextTestimonial}
                    data-testid="testimonial-next"
                    className="w-10 h-10 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-white/30 transition-colors duration-200"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <span className="text-xs text-slate-600 font-mono ml-2">
                    {currentTestimonial + 1} / {testimonials.length}
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
